import {
  createRecommendedHomeLayout,
  HOME_BLOCK_KEYS,
  type EventHomeLayout,
  type EventModuleKey,
  type CustomHomeSection,
  type HomeBlockKey,
  type HomeBlockOverride,
} from '@conference/shared';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function cloneBlocks(value: EventHomeLayout | null | undefined): Record<string, HomeBlockOverride> {
  if (!isPlainObject(value?.blocks)) return {};
  return Object.fromEntries(
    Object.entries(value.blocks).map(([key, block]) => [key, isPlainObject(block) ? { ...block } : {}]),
  );
}

function materialize(layout: EventHomeLayout): EventHomeLayout {
  return {
    version: 1,
    blocks: cloneBlocks(layout),
    custom_sections: layout.custom_sections?.map((section) => ({
      ...section,
      visibility: [...section.visibility],
      action: section.action ? { ...section.action } : section.action,
    })),
  };
}

function customSectionId(): string {
  return `home-section-${globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
}

export function addCustomHomeSection(layout: EventHomeLayout): EventHomeLayout {
  const next = materialize(layout);
  const orders = [
    ...Object.values(next.blocks ?? {}).flatMap((block) => typeof block.order === 'number' ? [block.order] : []),
    ...(next.custom_sections ?? []).map(({ order }) => order),
  ];
  const section: CustomHomeSection = {
    id: customSectionId(),
    title: 'New section',
    body_markdown: 'Add your content here.',
    enabled: true,
    order: (Math.max(0, ...orders) || 0) + 10,
    visibility: ['before', 'during', 'after'],
  };
  next.custom_sections = [...(next.custom_sections ?? []), section];
  return next;
}

export function updateCustomHomeSection(
  layout: EventHomeLayout,
  id: string,
  edit: Partial<CustomHomeSection>,
): EventHomeLayout {
  const next = materialize(layout);
  next.custom_sections = (next.custom_sections ?? []).map((section) => (
    section.id === id ? { ...section, ...edit } : section
  ));
  return next;
}

export function duplicateCustomHomeSection(layout: EventHomeLayout, id: string): EventHomeLayout {
  const next = materialize(layout);
  const source = next.custom_sections?.find((section) => section.id === id);
  if (!source) return next;
  const duplicate = { ...source, id: customSectionId(), title: `${source.title} copy`, order: source.order + 1 };
  next.custom_sections = [...(next.custom_sections ?? []), duplicate];
  return next;
}

export function deleteCustomHomeSection(layout: EventHomeLayout, id: string): EventHomeLayout {
  const next = materialize(layout);
  next.custom_sections = (next.custom_sections ?? []).filter((section) => section.id !== id);
  return next;
}

type HomePosition = { kind: 'block' | 'custom'; id: string; order: number };

function orderedHomePositions(layout: EventHomeLayout): HomePosition[] {
  const defaults = createRecommendedHomeLayout().blocks ?? {};
  const blocks = HOME_BLOCK_KEYS.map((key) => ({
    kind: 'block' as const,
    id: key,
    order: layout.blocks?.[key]?.order ?? defaults[key]?.order ?? 0,
  }));
  const custom = (layout.custom_sections ?? []).map((section) => ({
    kind: 'custom' as const,
    id: section.id,
    order: section.order,
  }));
  return [...blocks, ...custom].sort((left, right) => (
    left.order - right.order
    || (left.kind === right.kind ? left.id.localeCompare(right.id) : left.kind === 'custom' ? -1 : 1)
  ));
}

export function canMoveCustomHomeSection(layout: EventHomeLayout, id: string, direction: 'up' | 'down'): boolean {
  const positions = orderedHomePositions(layout);
  const index = positions.findIndex((position) => position.kind === 'custom' && position.id === id);
  return direction === 'up' ? index > 0 : index >= 0 && index < positions.length - 1;
}

export function moveCustomHomeSection(
  layout: EventHomeLayout,
  id: string,
  direction: 'up' | 'down',
): EventHomeLayout {
  const positions = orderedHomePositions(layout);
  const index = positions.findIndex((position) => position.kind === 'custom' && position.id === id);
  const neighborIndex = direction === 'up' ? index - 1 : index + 1;
  if (index < 0 || neighborIndex < 0 || neighborIndex >= positions.length) return materialize(layout);

  const neighbor = positions[neighborIndex];
  const outer = positions[direction === 'up' ? neighborIndex - 1 : neighborIndex + 1];
  const order = outer
    ? (neighbor.order + outer.order) / 2
    : neighbor.order + (direction === 'up' ? -10 : 10);
  const next = materialize(layout);
  next.custom_sections = next.custom_sections?.map((section) => section.id === id ? { ...section, order } : section);
  return next;
}

export function orderedHomeBlockKeys(layout: EventHomeLayout): HomeBlockKey[] {
  const defaults = createRecommendedHomeLayout().blocks ?? {};
  return [...HOME_BLOCK_KEYS].sort((left, right) => {
    const leftOrder = layout.blocks?.[left]?.order ?? defaults[left]?.order ?? 0;
    const rightOrder = layout.blocks?.[right]?.order ?? defaults[right]?.order ?? 0;
    return leftOrder - rightOrder || HOME_BLOCK_KEYS.indexOf(left) - HOME_BLOCK_KEYS.indexOf(right);
  });
}

export function normalizeEventHomeLayout(value: unknown): EventHomeLayout {
  if (!isPlainObject(value) || Object.keys(value).length === 0) return {};
  const version = value.version === 1 ? 1 : undefined;
  const blocks = isPlainObject(value.blocks)
    ? Object.fromEntries(Object.entries(value.blocks).map(([key, block]) => [key, isPlainObject(block) ? { ...block } : {}]))
    : {};
  const customSections = Array.isArray(value.custom_sections)
    ? value.custom_sections.filter(isPlainObject).map((section) => ({ ...section } as unknown as CustomHomeSection))
    : undefined;
  return { ...(version ? { version } : {}), blocks, ...(customSections ? { custom_sections: customSections } : {}) };
}

export function updateHomeBlock(
  layout: EventHomeLayout,
  key: HomeBlockKey,
  edit: HomeBlockOverride,
): EventHomeLayout {
  const next = materialize(layout);
  next.blocks = { ...next.blocks, [key]: { ...next.blocks?.[key], ...edit } };
  return next;
}

export function moveHomeBlock(
  layout: EventHomeLayout,
  key: HomeBlockKey,
  direction: 'up' | 'down',
): EventHomeLayout {
  const base = createRecommendedHomeLayout();
  const next = materialize(layout);
  const known = Object.entries(base.blocks ?? {})
    .map(([blockKey, defaults]) => ({
      key: blockKey as HomeBlockKey,
      order: next.blocks?.[blockKey]?.order ?? defaults.order ?? 0,
    }))
    .sort((left, right) => left.order - right.order);
  const index = known.findIndex((block) => block.key === key);
  const neighborIndex = direction === 'up' ? index - 1 : index + 1;
  if (index < 0 || neighborIndex < 0 || neighborIndex >= known.length) return next;

  [known[index], known[neighborIndex]] = [known[neighborIndex], known[index]];
  for (const [position, block] of known.entries()) {
    next.blocks = { ...next.blocks, [block.key]: { ...next.blocks?.[block.key], order: (position + 1) * 10 } };
  }
  return next;
}

export function toggleQuickAction(
  layout: EventHomeLayout,
  moduleKey: EventModuleKey,
): EventHomeLayout {
  const next = materialize(layout);
  const current = next.blocks?.quick_actions?.module_keys ?? [];
  const selected = current.includes(moduleKey)
    ? current.filter((key) => key !== moduleKey)
    : current.length < 3 ? [...current, moduleKey] : current;
  next.blocks = {
    ...next.blocks,
    quick_actions: { ...next.blocks?.quick_actions, module_keys: selected },
  };
  return next;
}

export function resetHomeLayout(): EventHomeLayout {
  return {};
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value)
      // The API uses `exclude_none=True` for sparse layout settings, so an
      // explicit UI null and an omitted optional field persist identically.
      .filter(([, entry]) => entry !== undefined && entry !== null)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, canonicalize(entry)]),
  );
}

export function homeLayoutSignature(layout: EventHomeLayout): string {
  return JSON.stringify(canonicalize(layout));
}
